import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { fmtTL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, Loader2, CheckCircle2 } from "lucide-react";

const TURLER = ["KDV", "MUHTASAR", "SGK", "DAMGA V", "GEÇİCİ VERGİ", "YILLIK BEYANNAME", "KURUMLAR"];
const CHUNK = 20;
const sel = "h-8 rounded-md border border-input bg-background px-1.5 text-xs";

function Badge({ r }) {
  if (!r.ok) return <span className="text-xs px-2 py-0.5 rounded bg-rose-100 text-rose-700">Okunamadı</span>;
  if (r.duplicate) return <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-700">Zaten yüklü</span>;
  if (r.match === "VKN" || r.match === "SİCİL") return <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">{r.match === "VKN" ? "Kimlik no ile eşleşti" : "SGK sicil ile eşleşti"}</span>;
  if (r.match === "İSİM") return <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">İsimle eşleşti</span>;
  return <span className="text-xs px-2 py-0.5 rounded bg-rose-100 text-rose-700">Eşleşmedi</span>;
}

export default function BulkTahakkukDialog({ open, onOpenChange, onDone }) {
  const [phase, setPhase] = useState("pick"); // pick | analyzing | review | saving | done
  const [rows, setRows] = useState([]);
  const [batch, setBatch] = useState("");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [clients, setClients] = useState([]);
  const [result, setResult] = useState(null);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPhase("pick"); setRows([]); setBatch(""); setResult(null);
    api.get("/clients").then((r) => setClients([...r.data].sort((a, b) => (a.unvan || "").localeCompare(b.unvan || "", "tr")))).catch(() => {});
  }, [open]);

  const analyze = async (fileList) => {
    const pdfs = [...fileList].filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (!pdfs.length) return toast.error("PDF dosyası seçilmedi");
    setPhase("analyzing");
    setProgress({ done: 0, total: pdfs.length });
    let bid = ""; const all = [];
    try {
      for (let i = 0; i < pdfs.length; i += CHUNK) {
        const fd = new FormData();
        pdfs.slice(i, i + CHUNK).forEach((f) => fd.append("files", f));
        if (bid) fd.append("batch_id", bid);
        const { data } = await api.post("/tahakkuk/bulk/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
        bid = data.batch_id;
        all.push(...data.rows);
        setProgress({ done: Math.min(i + CHUNK, pdfs.length), total: pdfs.length });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dosyalar okunamadı");
      setPhase("pick");
      return;
    }
    setBatch(bid);
    setRows(all.map((r) => ({ ...r, selected: r.ok && !!r.client_id && !!r.tur && !!r.period && !r.duplicate })));
    setPhase("review");
  };

  const upd = (id, patch) => setRows((rs) => rs.map((r) => (r.file_id === id ? { ...r, ...patch } : r)));

  const chosen = useMemo(() => rows.filter((r) => r.selected), [rows]);
  const incomplete = chosen.filter((r) => !r.client_id || !r.tur || !r.period).length;

  const commit = async () => {
    if (!chosen.length) return toast.error("Kaydedilecek satır seçilmedi");
    if (incomplete) return toast.error(`${incomplete} seçili satırda mükellef, tür veya dönem eksik`);
    setPhase("saving");
    try {
      const { data } = await api.post("/tahakkuk/bulk/commit", {
        batch_id: batch,
        items: chosen.map((r) => ({ file_id: r.file_id, filename: r.filename, client_id: r.client_id, tur: r.tur, period: r.period, tutar: r.tutar, vade: r.vade, sicil: r.sicil })),
      });
      setResult(data);
      setPhase("done");
      window.dispatchEvent(new Event("tahakkuk:updated"));
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
      setPhase("review");
    }
  };

  const counts = useMemo(() => ({
    ok: rows.filter((r) => r.ok && r.match && !r.duplicate).length,
    dup: rows.filter((r) => r.duplicate).length,
    bad: rows.filter((r) => !r.ok || !r.match).length,
  }), [rows]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Toplu Tahakkuk Yükle</DialogTitle></DialogHeader>

        {phase === "pick" && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); analyze(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()} data-testid="bulk-dropzone"
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${drag ? "border-primary bg-accent" : "border-border hover:bg-accent/40"}`}>
            <Upload size={28} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm font-medium">Tahakkuk PDF'lerinin hepsini buraya sürükleyin</p>
            <p className="text-xs text-muted-foreground mt-1">veya seçmek için tıklayın · KDV, Muhtasar, SGK, Geçici Vergi, Yıllık Beyanname, Kurumlar fişleri</p>
            <p className="text-xs text-muted-foreground mt-3">Sistem her PDF'in içinden mükellefi, türü ve dönemi okur; kaydetmeden önce listeyi size gösterir.</p>
            <input ref={fileRef} type="file" accept=".pdf,application/pdf" multiple className="hidden" data-testid="bulk-file-input"
              onChange={(e) => { if (e.target.files?.length) analyze(e.target.files); e.target.value = ""; }} />
          </div>
        )}

        {phase === "analyzing" && (
          <div className="py-14 text-center text-muted-foreground">
            <Loader2 size={26} className="mx-auto mb-3 animate-spin" />
            <p className="text-sm">PDF'ler okunuyor... {progress.done} / {progress.total}</p>
          </div>
        )}

        {(phase === "review" || phase === "saving") && (<>
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="px-2.5 py-1 rounded bg-emerald-100 text-emerald-700">{counts.ok} eşleşti</span>
            {counts.dup > 0 && <span className="px-2.5 py-1 rounded bg-slate-200 text-slate-700">{counts.dup} zaten yüklü</span>}
            {counts.bad > 0 && <span className="px-2.5 py-1 rounded bg-rose-100 text-rose-700">{counts.bad} kontrol gerekiyor</span>}
            <span className="ml-auto text-muted-foreground">{chosen.length} satır kaydedilecek</span>
          </div>
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-secondary/60 uppercase text-muted-foreground"><tr>
                <th className="px-2 py-2 w-8"></th><th className="text-left px-2 py-2 font-medium">Dosya</th><th className="text-left px-2 py-2 font-medium">Tür</th>
                <th className="text-left px-2 py-2 font-medium">Dönem</th><th className="text-left px-2 py-2 font-medium">Mükellef</th>
                <th className="text-right px-2 py-2 font-medium">Tutar</th><th className="text-left px-2 py-2 font-medium">Durum</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.file_id} data-testid={`bulk-row-${r.file_id}`} className={`border-t border-border ${!r.ok ? "bg-rose-50/60 dark:bg-rose-500/5" : r.selected ? "" : "opacity-70"}`}>
                    <td className="px-2 py-2"><input type="checkbox" disabled={!r.ok} checked={!!r.selected} onChange={(e) => upd(r.file_id, { selected: e.target.checked })} /></td>
                    <td className="px-2 py-2 max-w-[200px]"><div className="flex items-center gap-1.5"><FileText size={13} className="text-rose-500 shrink-0" /><span className="truncate" title={r.filename}>{r.filename}</span></div></td>
                    {r.ok ? (<>
                      <td className="px-2 py-2"><select className={sel} value={r.tur || ""} onChange={(e) => upd(r.file_id, { tur: e.target.value })}><option value="">Seçin</option>{TURLER.map((t) => <option key={t} value={t}>{t}</option>)}</select></td>
                      <td className="px-2 py-2"><input type="month" className={sel} value={r.period || ""} onChange={(e) => upd(r.file_id, { period: e.target.value })} /></td>
                      <td className="px-2 py-2 min-w-[240px]">
                        <select className={`${sel} w-full`} value={r.client_id || ""} onChange={(e) => { const c = clients.find((x) => x.id === e.target.value); upd(r.file_id, { client_id: e.target.value, client_unvan: c?.unvan, selected: !!e.target.value }); }}>
                          <option value="">— Mükellef seçin —</option>
                          {r.candidates?.length > 0 && r.candidates.map((c) => <option key={`c${c.client_id}`} value={c.client_id}>★ {c.unvan}</option>)}
                          {clients.map((c) => <option key={c.id} value={c.id}>{c.unvan}</option>)}
                        </select>
                        {!r.client_id && <div className="text-[11px] text-muted-foreground mt-0.5 truncate" title={r.unvan_fis}>Fişte: {r.unvan_fis || r.vkn || "-"}</div>}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">{r.tutar != null ? fmtTL(r.tutar) : "-"}</td>
                      <td className="px-2 py-2"><Badge r={r} />{r.warn && <div className="text-[11px] text-amber-700 mt-0.5 max-w-[260px]">{r.warn}</div>}</td>
                    </>) : (<>
                      <td colSpan={4} className="px-2 py-2 text-rose-700">{r.error}</td>
                      <td className="px-2 py-2"><Badge r={r} /></td>
                    </>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={phase === "saving"}>Vazgeç</Button>
            <Button data-testid="bulk-commit" onClick={commit} disabled={phase === "saving" || !chosen.length}>
              {phase === "saving" ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Kaydediliyor...</> : `Seçilen ${chosen.length} Tahakkuku Kaydet`}
            </Button>
          </DialogFooter>
        </>)}

        {phase === "done" && result && (
          <div className="py-6 space-y-3">
            <div className="flex items-center gap-2 text-emerald-700 font-medium"><CheckCircle2 size={20} /> {result.saved} tahakkuk kaydedildi</div>
            {result.notes?.length > 0 && (
              <div className="text-sm"><div className="font-medium mb-1">Beyanname durumları güncellendi ({result.notes.length}):</div>
                <ul className="list-disc pl-5 text-muted-foreground max-h-48 overflow-y-auto">{result.notes.map((n, i) => <li key={i}>{n}</li>)}</ul></div>
            )}
            {result.failed?.length > 0 && (
              <div className="text-sm text-rose-700"><div className="font-medium mb-1">Kaydedilemeyen {result.failed.length} dosya:</div>
                <ul className="list-disc pl-5">{result.failed.map((f, i) => <li key={i}>{f.filename}: {f.error}</li>)}</ul></div>
            )}
            <DialogFooter><Button onClick={() => onOpenChange(false)}>Kapat</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
